import studyFlowAccessMiddleware from "@/middleware/studyFlowAccess";
import studyAccessPageMiddleware from "@/middleware/studyAccessPage";
import type { Router } from "vue-router";
import type { Pinia } from "pinia";
import { setLocale } from "@/vue-i18n/vue-i18n";
import { useUserStore } from "@/stores/userStore";
import { useAuthStore } from "@/stores/authStore";
import type { UseIntercom } from "@/plugins/useIntercom";
import FlowParticipationsApi from "@/data/api-layer/flowParticipationsApi";
import { getUseToast } from "../plugins/useToast";

// Add current locale to the redirect path and replace it if already present
// handles: /signup | signup | /en-gb/signup | en-gb/signup
// NOTE: We use i18n.locale.value instead of $route.params.locale, because the first, unlike the second is always set
// for example, /login doesn't have a locale parameter in the URL, but the i18n locale will be set to the site fallback locale
export const addLocaleToRedirectPath = (redirectPath: string, i18n: any): string => {
   // remove any leading slashes from the path
   let newRedirectPath = redirectPath.replace(/^\/+/g, "");

   // matches two letters, dash, two more letters (e.g., en-gb)
   const localeRegex = /^[a-zA-Z]{2}-[a-zA-z]{2}/i;

   // check if the path already contains a locale
   const hasLocale = localeRegex.test(newRedirectPath);

   if (hasLocale) {
      // If the path already contains a locale, replace it with the current locale
      newRedirectPath = newRedirectPath.replace(localeRegex, i18n.locale.value);
   } else {
      // If the path doesn't have a locale, prepend the current locale
      newRedirectPath = `${i18n.locale.value}/${newRedirectPath}`;
   }

   return `/${newRedirectPath}`; // add leading slash back in
};

function goToHomeOrRequestedRedirect(to, next, forwardQuery, i18n) {
   // is there a redirect query param? > redirect to that url if there is one
   const params = { locale: to.params?.locale };

   if (to.query.redirect) {
      const forwardPath = addLocaleToRedirectPath(to.query.redirect, i18n);

      next({ path: forwardPath, query: forwardQuery });
   }
   // no redirect query param? > redirect to home
   else {
      next({ name: "home", query: forwardQuery, params });
   }
   return;
}

function configureRoutes(router: Router, i18n: any, intercom: UseIntercom, pinia: Pinia, clientSettings) {
   // Route guards start
   // -----------------------------------------

   // Initialise appStore
   // IMPORTANT: for the store to work, configureRoutes should always be called after app.use(pinia)
   const userStore = useUserStore();
   const authStore = useAuthStore();
   const useToast = getUseToast(i18n.t);

   router.beforeEach(async (to, from, next) => {
      // Only implement the navigation guards when not pre-rendering
      // (Only public pages should be pre-rendered & the 'user' will never be logged in)
      if (import.meta.env.SSR) {
         next();
         return;
      }

      // Token refresh
      // -------------------------------
      // if page has been reloaded / on route change, try to fetch a new JWT with the refresh cookie (Not when pre-rendering)
      if (!authStore.isAuthTokenAndDCValid()) {
         // On login, a token is set. If a device code is needed, refresh will be called on the route change /login > /device-code
         // refresh requests fail when no device code is present, and if we destroyed the token, route guards would prevent us reaching the device code page
         const preventDeleteTokenOnRefreshFail = from.name === "login" && to.name === "device-code";
         await authStore.refreshTheToken(preventDeleteTokenOnRefreshFail);
      }

      // check if the param locale is different from the current locale (for example, if the user has changed the locale in the URL or via UI)
      const isParamLocaleDifferentFromCurrent = to.params.locale !== i18n.locale.value;

      // check whether the param locale is supported on the portal
      const isParamLocaleSuported = clientSettings.supportedLocales.find(({ id }) => id === to.params.locale);

      // If the user is in the middle of a locale switch
      // i.e the query param locale is set to /es-us/home and the Vue App Locale was en-gb
      // then update the locale and re-fetch content shared across pages
      if (to.params.locale && isParamLocaleDifferentFromCurrent && isParamLocaleSuported) {
         await setLocale(to.params.locale as string, i18n);
      }
      // if the param locale is unsupported, redirect to the fallback locale with query params
      else if (to.params.locale && !isParamLocaleSuported) {
         next({
            name: to.name,
            params: { ...to.params, locale: i18n.fallbackLocale.value },
            query: to.query,
         });
         return;
      }
      // if the param locale is not set and the user is logged-in, use the user preferred locale
      else if (!to.params.locale && authStore.isAuthTokenAndDCValid()) {
         try {
            await userStore.setUser();
            const userPreferredLocale = userStore.user.locale.id;

            // if the preferred locale is different from the current portal locale
            if (userPreferredLocale !== i18n.locale.value) {
               // redirect to the user preferred locale
               next({ name: to.name, params: { ...to.params, locale: userPreferredLocale } });
               return;
            }
         } catch (e) {
            console.error("Error while fetching user data", e);
         }
      }

      // Intercom hooks
      // -------------------------------
      // Calling the update method with some new user data will trigger the JavaScript to look for new messages that should be displayed to the current user
      if (intercom.isActive) {
         const userEmail = authStore.getUserEmail() || null;
         // if the user email is set, update the intercom
         if (userEmail) {
            intercom.updateIntercom(userEmail, i18n.locale.value, userStore.user?.intercomHash || null);
         }
      }

      // route guards
      // -------------------------------
      // (No loggedIn routes should be pre-rendered)
      //let forward_path = to.path;
      // const forward_query = to.query;

      // forward query = same as to.query but without redirect query param
      const forwardQuery = { ...to.query };
      if (forwardQuery.redirect) {
         delete forwardQuery.redirect;
      }

      // logged OUT
      if (!authStore.isAuthTokenAndDCValid()) {
         // Set state.isImpersonating to false
         // The reason to handle state.isImpersonating here is because the impersonation banner must be added to app.vue
         // to ensure it appears on each page, but isImpersonating needs to be updated on logout and on route change
         // 'onBeforeRouteUpdate' cannot be accessed from app.vue
         userStore.isImpersonating = false;

         // logged out and trying to access protected page > redirect to login (with query params)
         // ex. /home > /login?redirect=/home or /home?some=query&params=here > /login?redirect=/home&some=query&params=here
         if (!to.meta?.isPublic) {
            authStore.destroyToken();
            useToast({ text: i18n.t("validation.refresh_error"), type: "error" });

            // add the redirect to the requested page
            forwardQuery.redirect = to.path;
            next({ name: "login", query: forwardQuery, params: { locale: to.params?.locale } });
            return;
         }
         // logged out and trying to access public page > continue to the page (without query params)
         else {
            // logged out, public page but the page is "device-code"
            if (to.path.includes("device-code")) {
               // user tried to access device-code page without a valid access token (i.e. possibly didnt login or the token expired)
               // in this case > redirect to login (include ALL query params including the redirect query param)
               if (authStore.getTokenStatus() !== "valid") {
                  next({ name: "login", query: to.query, params: { locale: to.params?.locale } });
                  return;
               }
               // user tried to access device-code page with a valid access token (i.e. they are logged in)
               // in this case > continue to device-code (include ALL query params including the redirect query param)
               else {
                  next();
                  return;
               }
            }
            // logged out and trying to access a /flow/ page > check access
            else if (to.path.includes("flow") && to.params.id) {
               return studyFlowAccessMiddleware(to, from, next, false);
            }
            // logged out, public page > continue to the page (without query params)
            else {
               next();
               return;
            }
         }
      }
      // logged IN
      else {
         // Set state.isImpersonating (boolean)
         // The reason to handle state.isImpersonating here is because the impersonation banner must be added to app.vue
         // to ensure it appears on each page, but isImpersonating needs to be updated on logout and on route change
         // 'onBeforeRouteUpdate' cannot be accessed from app.vue
         userStore.isImpersonating = authStore.getIsImpersonating();

         // logged in and trying to access an auth-type page like: register, login, device-code, etc > redirect to home (with query params)
         // scenario: User already logged in but they clicked on the complete register link in their email which was: /complete/<token>?redirect=flow/123 > /flow/1234
         if (to.meta.loggedOutOnly || to.path.includes("device-code")) {
            goToHomeOrRequestedRedirect(to, next, forwardQuery, i18n);
            return;
         }
         // trying to access study access page
         else if (to.name === "study-access") {
            return studyAccessPageMiddleware(to, from, next);
         }
         // trying to access core profile flow but the core profile feature is disabled > redirect to home page
         else if (to.path.includes("flow/coreprofile") && !clientSettings.requireCoreProfileOnLogin) {
            console.info("Core profile flow is disabled, redirecting to home");
            next({ name: "home", query: forwardQuery, params: { locale: to.params?.locale } });
            return;
         } else if (
            /*
            If the route is not the Core Profile Flow & the feature is enabled,
            check that the ppt has completed it
            If it has not been completed, redirect to the core profile flow
            if it has been completed, continue to the requested page (checking access if it's a /flow/ page)
            */
            !to.path.includes("flow/coreprofile") &&
            clientSettings.requireCoreProfileOnLogin &&
            !userStore.isCoreProfileFlowComplete
         ) {
            console.info("route is not the Core Profile Flow & the feature is enabled");

            let isCoreProfileFlowComplete = false;
            // Fetch the core profile flow participation to determine if it has been completed
            try {
               // Direct request without saving to the store, as this only checks if the coreprofile flow has been completed
               const coreFlowParticipationResp = await FlowParticipationsApi.getParticipation("coreprofile");
               isCoreProfileFlowComplete = !!coreFlowParticipationResp?.data.reachedEndAt;
            } catch (e: any) {
               if (e.response?.status !== 404) {
                  console.error(`Error while fetching flow participation: ${e}`);
               }
            }
            // core profile not complete, redirect to core profile flow (carry through all query params)
            if (!isCoreProfileFlowComplete) {
               console.info("Core profile flow not complete, redirecting to core profile flow");
               forwardQuery.redirect = to.path;
               next({ name: "flow", query: forwardQuery, params: { id: "coreprofile", locale: to.params?.locale } });
            } // core profile complete and trying to access a flow (not impersonate route)
            else if (isCoreProfileFlowComplete && to.path.includes("flow/") && !to.path.includes("access/flow/")) {
               console.info("Core profile complete, trying to access a /flow/ route");
               return studyFlowAccessMiddleware(to, from, next, true);
            }
            // core profile complete, non flow route
            else {
               console.info("Core profile flow complete, continuing to requested page");
               userStore.isCoreProfileFlowComplete = true;
               next();
            }

            return;
         }
         // logged in and trying to access a flow (not impersonate route)
         else if (to.path.includes("flow/") && to.params.id !== "coreprofile" && !to.path.includes("access/flow/")) {
            return studyFlowAccessMiddleware(to, from, next, true);
         }

         // default logged in
         else {
            next();
            return;
         }
      }
   });

   router.afterEach(async (to) => {
      // Check for the situation where a user session has ended by timing out rather than logging out
      // In this situation they are redirected to /login, but the app is not re-loaded, so we will clear any remaining user data
      const newRouteIsPublic = to.meta?.isPublic;
      if (newRouteIsPublic && !authStore.authTokenExists && !!userStore.user) {
         await nextTick();
         userStore.clearAllUserData();
      }
   });
}

export { configureRoutes };
